import React, { Fragment } from 'react'

import { Box } from '@gousto-internal/citrus-react'
import { AppBanner } from 'AppBanner'
import { CancelOrderModalContainer } from 'CancelOrderModal'
import { DuplicateOrderModalWrapperContainer } from 'DuplicateOrderModal'
import { SimpleHeader } from 'Header/SimpleHeader'
import Link from 'Link'
import { Login } from 'Login'
import ModalPanel from 'Modal/ModalPanel'
import Overlay from 'Overlay'
import Svg from 'Svg'
import { TestUserBannerContainer } from 'TestUserBanner'
import classNames from 'classnames'
import { Button } from 'goustouicomponents'
import PropTypes from 'prop-types'

import { FlagProblematicRendering } from '@library/e2e-util-problematic-rendering'

import * as trackingKeys from 'actions/trackingKeys'
import { client } from 'config/routes'
import Account from 'routes/Account/Account'
import {
  OrderSkipOSRViewContainer,
  SubPauseOSRViewContainer,
} from 'routes/Account/MyDeliveries/OrdersList/OnScreenRecovery'
import { SearchButton } from 'routes/Menu/components/Search/SearchButton/SearchButton'
import { onEnter } from 'utils/accessibility'

import { BoxPricesLink, HowItWorksDropdown, OurRecipesDropdown, RecipeBoxesLink } from './DesktopComponents'
import { MobileMenu } from './MobileMenu'
import { getDeepClonedMenuItems } from './menuItemsHelper'

import css from './Header.css'

const BUTTON_STATE_CLASSES = {
  loggingOut: css.loggingOut,
  loggedIn: css.loggedIn,
  loggedOut: css.loggedOut,
}

class Header extends React.PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      mobileMenuOpen: false,
      loginPending: false,
      logoutPending: false,
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { isAuthenticated } = this.props
    if (isAuthenticated !== nextProps.isAuthenticated) {
      if (nextProps.isAuthenticated) {
        this.setState({ loginPending: true })
      } else {
        this.setState({ logoutPending: true })
      }
      this.hideMobileMenu()
    }
  }

  componentDidUpdate(prevProps) {
    const { loginPending, logoutPending } = this.state
    const { isHelpPreLoginOpen, trackHelpPreLoginModalDisplayed } = this.props

    if (loginPending) {
      window.setTimeout(() => {
        this.setState({ loginPending: false })
      }, 1000)
    }
    if (logoutPending) {
      window.setTimeout(() => {
        this.setState({ logoutPending: false })
      }, 1000)
    }

    if (prevProps.isHelpPreLoginOpen === false && isHelpPreLoginOpen === true) {
      trackHelpPreLoginModalDisplayed()
    }
  }

  showHelpPreLogin = () => {
    const { helpPreLoginVisibilityChange } = this.props
    helpPreLoginVisibilityChange(true)
  }

  shouldShowDoubleDeckerBar = () => {
    const { routing, doubleDeckerExperimentEnabled } = this.props
    const pathName = routing?.locationBeforeTransitions?.pathname
    const isMenuPath = pathName === client.menu

    return isMenuPath && doubleDeckerExperimentEnabled
  }

  onClose = () => {
    const { loginVisibilityChange, helpPreLoginVisibilityChange } = this.props
    helpPreLoginVisibilityChange(false)
    loginVisibilityChange(false)
    this.setState({ loginPending: false })
  }

  onCloseCancelBoxModal = () => {
    const { closeBoxModalVisibilityChange } = this.props
    closeBoxModalVisibilityChange(false)
  }

  onLoginClick = (e) => {
    const { isAuthenticated, loginVisibilityChange } = this.props

    if (!isAuthenticated) {
      e.stopPropagation()
      loginVisibilityChange(true)
    }
  }

  getMenuItems = (device, path) => {
    const { isAuthenticated, promoCodeUrl, fromJoin } = this.props
    const menuItems = getDeepClonedMenuItems()
    let pathLocal = path
    if (path.indexOf('/') === -1) {
      pathLocal = `/${pathLocal}`
    }

    Object.keys(menuItems).forEach((menuItem) => {
      const currentMenuItem = menuItems[menuItem]

      if (pathLocal.indexOf(currentMenuItem.url) > -1) {
        currentMenuItem.disabled = true
      }
    })

    const homeMenuItem = { ...menuItems.home }

    if (isAuthenticated) {
      homeMenuItem.url = client.myGousto
    } else if (promoCodeUrl) {
      homeMenuItem.url = `/${promoCodeUrl}`
    } else if (path.includes('check-out')) {
      homeMenuItem.url = client.menu
    } else if (path.includes('join') || fromJoin === 'join') {
      homeMenuItem.url = client.join
    }

    const menuItemMenu = {
      ...menuItems.menu,
      url: isAuthenticated ? client.deliveryDetails : menuItems.menu.url,
      isParent: true,
    }

    const desktopItems = [
      isAuthenticated && menuItemMenu,
      isAuthenticated && menuItems.referFriend,
      isAuthenticated && menuItems.sustainability,
      isAuthenticated && menuItems.help,
    ].filter((item) => item)

    const myGousto = [menuItems.myGousto]
    const rateMyRecipes = [menuItems.rateMyRecipes]
    const deliveries = [menuItems.deliveries]
    const subscription = [menuItems.subscription]
    const details = [menuItems.details]
    const referFriend = [menuItems.referFriend]

    const boxPricesParent = {
      ...menuItems.boxPrices,
      isParent: true,
    }

    const recipeBoxes = {
      ...menuItems.recipeBoxes,
      isParent: true,
    }

    const howItWorksParent = {
      ...menuItems.howItWorks,
      isParent: true,
      name: 'How It Works',
    }

    const howGoustoWorks = {
      name: 'How Gousto Works',
      url: client.howGoustoWorks,
      clientRouted: true,
      tracking: 'HowGoustoWorks Clicked',
      isChild: true,
    }

    const mealPlanning = {
      name: 'Menus and Meal Planning',
      url: client.goustoMealPlanning,
      clientRouted: true,
      tracking: 'GoustoMealPlanning Clicked',
      isChild: true,
    }

    const ourSuppliers = {
      name: 'Our Suppliers',
      url: client.ourSuppliers,
      clientRouted: false,
      tracking: 'OurSuppliersNavigation Clicked',
      isChild: true,
    }

    const sustainability = {
      ...menuItems.sustainability,
      isChild: true,
    }

    const ourRecipesParent = {
      ...menuItems.ourRecipes,
      isParent: true,
      name: 'Our Recipes',
    }

    const thisWeeksMenu = {
      name: 'This Week\'s Menu',
      url: client.menu,
      clientRouted: true,
      tracking: 'ThisWeeksMenu Clicked',
      isChild: true,
    }

    const allGoustoRecipes = {
      name: 'All Gousto Recipes',
      url: client.cookbookAllRecipes,
      clientRouted: false,
      tracking: 'AllGoustoRecipes Clicked',
      isChild: true,
    }

    let mobileMenu = []

    if (isAuthenticated) {
      const homeParent = {
        ...homeMenuItem,
        isParent: true,
      }
      const chooseRecipesChild = {
        ...menuItems.menu,
        url: isAuthenticated ? client.deliveryDetails : menuItems.menu.url,
        isChild: true,
      }
      const sustainabilityChild = {
        ...menuItems.sustainability,
        isChild: true,
      }
      const helpChild = {
        ...menuItems.help,
        isChild: true,
      }

      mobileMenu = myGousto.concat(
        deliveries,
        subscription,
        details,
        referFriend,
        rateMyRecipes,
        homeParent,
        chooseRecipesChild,
        sustainabilityChild,
        helpChild,
      )
    } else {
      mobileMenu = mobileMenu.concat(
        homeMenuItem,
        recipeBoxes,
        howItWorksParent,
        howGoustoWorks,
        mealPlanning,
        ourSuppliers,
        sustainability,
        boxPricesParent,
        ourRecipesParent,
        thisWeeksMenu,
        allGoustoRecipes,
      )
    }

    return device === 'mobile' ? mobileMenu : desktopItems
  }

  handleQuery = () => {
    let route = ''
    let fromWizard = false
    const { routing, path } = this.props
    if (routing && routing.locationBeforeTransitions) {
      if (routing.locationBeforeTransitions.pathname) {
        route = routing.locationBeforeTransitions.pathname
      }
      if (
        routing.locationBeforeTransitions.query &&
        routing.locationBeforeTransitions.query.from_wizard
      ) {
        fromWizard = true
      }
    }
    const newPath = path || route

    return { fromWizard, newPath }
  }

  onLogoutClick = () => {
    const { logoutUser } = this.props
    logoutUser()
  }

  showMobileMenu = () => {
    this.setState({ mobileMenuOpen: true })
  }

  hideMobileMenu = () => {
    this.setState({ mobileMenuOpen: false })
  }

  renderAuthLink = () => {
    const { isAuthenticated } = this.props
    const { logoutPending } = this.state

    let buttonState
    let button

    if (logoutPending) {
      buttonState = 'loggingOut'
      button = (
        <button
          type="button"
          className={classNames(css.btn, {
            [css.experimentBtn]: this.shouldShowDoubleDeckerBar(),
          })}
        >
          You&#8217;re logged out
          <span className={css.confirm} />
        </button>
      )
    } else if (isAuthenticated) {
      buttonState = 'loggedIn'
      button = (
        <Link
          key={client.myGousto}
          to={client.myGousto}
          className={classNames(css.btn, {
            [css.experimentBtn]: this.shouldShowDoubleDeckerBar(),
          })}
          clientRouted
          data-testing="myGoustoButtonLink"
        >
          My Gousto
        </Link>
      )
    } else {
      buttonState = 'loggedOut'
      button = (
        <button
          type="button"
          className={classNames(css.btn, {
            [css.experimentBtn]: this.shouldShowDoubleDeckerBar(),
          })}
          data-testing="loginButton"
        >
          Login
        </button>
      )
    }
    const logoutLink = (
      <span
        role="button"
        tabIndex="0"
        className={classNames(css.linkDesktop, {
          [css.experimentLink]: this.shouldShowDoubleDeckerBar(),
        })}
        onClick={this.onLogoutClick}
        onKeyDown={onEnter(this.onLogoutClick)}
        data-testing="logoutButton"
      >
        Logout
      </span>
    )

    return (
      <span
        role="button"
        tabIndex="0"
        className={classNames(css.authButtonsContainer, BUTTON_STATE_CLASSES[buttonState])}
        onClick={this.onLoginClick}
        onKeyDown={onEnter(this.onLoginClick)}
      >
        {button}
        {isAuthenticated && logoutLink}
      </span>
    )
  }

  renderMenuItems = (menu, hideNav) => {
    const { isAuthenticated, trackNavigationClick } = this.props

    if (hideNav) {
      return null
    }

    const helpNavTrackingData = {
      actionType: trackingKeys.clickHelpNavigation,
      seCategory: 'help',
      logged_in: isAuthenticated,
    }

    return menu.map((menuItem) => {
      if (menuItem.disabled) {
        return (
          <span key={menuItem.name} className={classNames(css.linkDesktop, css.disabled)}>
            {menuItem.fullWidthPrefix && (
              <span className={css.fullWidthPrefix}>{menuItem.fullWidthPrefix}</span>
            )}
            {menuItem.name}
          </span>
        )
      }
      const isHelpPreloginNeeded = menuItem.name.toLowerCase() === 'help'
      if (isHelpPreloginNeeded && !isAuthenticated) {
        return (
          <span
            key={menuItem.name}
            className={classNames(css.linkDesktop, {
              [css.experimentLink]: this.shouldShowDoubleDeckerBar(),
            })}
            data-test="help-link"
            role="button"
            tabIndex="0"
            onClick={() => {
              this.showHelpPreLogin()
              trackNavigationClick(helpNavTrackingData)
            }}
            onKeyDown={onEnter(this.showHelpPreLogin)}
          >
            {menuItem.name}
          </span>
        )
      }

      const isHelpLink = menuItem.name === 'Help'
      const trackingData = isHelpLink ? helpNavTrackingData : { actionType: menuItem.tracking }

      return (
        <Link
          key={menuItem.name}
          data-optimizely={isHelpLink ? 'desktop-header-help-link' : null}
          to={menuItem.url}
          className={classNames(css.linkDesktop, {
            [css.experimentLink]: this.shouldShowDoubleDeckerBar(),
          })}
          clientRouted={menuItem.clientRouted}
          tracking={() => trackNavigationClick(trackingData)}
        >
          {menuItem.fullWidthPrefix && (
            <span className={css.fullWidthPrefix}>{menuItem.fullWidthPrefix}</span>
          )}
          {menuItem.name}
        </Link>
      )
    })
  }

  onUseAppClick = () => {
    const { trackNavigationClick } = this.props
    trackNavigationClick({ actionType: 'UseAppHeaderCta Clicked' })
    window.location.assign('/apps')
  }

  renderLoginModal = () => {
    const { isHelpPreLoginOpen, isLoginOpen, trackContinueAsNewCustomer } = this.props

    return (
      <Overlay
        open={Boolean(isLoginOpen || isHelpPreLoginOpen)}
        contentClassName={css.modalOverlay}
        from="top"
      >
        <ModalPanel
          closePortal={this.onClose}
          className={css.modal}
          containerClassName={css.modalContainer}
          disableOverlay
          isNarrow
        >
          <Login
            title={isHelpPreLoginOpen ? 'We can help you faster if you\'re logged in' : 'Login'}
          />
          {isHelpPreLoginOpen ? (
            <Link
              to={client.helpCentre}
              data-optimizely="new-customer-help-link"
              clientRouted={false}
              className={css.continueAsNewCustomerLink}
              tracking={trackContinueAsNewCustomer}
            >
              Continue as new customer
            </Link>
          ) : null}
        </ModalPanel>
      </Overlay>
    )
  }

  render() {
    const {
      fromJoin,
      disabled,
      simple,
      isAuthenticated,
      serverError,
      title,
      promoCodeUrl,
      path,
      trackNavigationClick,
      routing,
      hasLoginModal,
      doubleDeckerExperimentEnabled,
    } = this.props
    const pathName = routing?.locationBeforeTransitions?.pathname
    const { mobileMenuOpen } = this.state
    const { fromWizard } = this.handleQuery()
    const joinPage = path.indexOf('join') > -1 || fromJoin
    const hideNav = fromWizard || joinPage || disabled || false
    const mobileMenuItems = this.getMenuItems('mobile', path)
    const homeElementMobile = mobileMenuItems.find((item) => item.name === 'Home')
    const desktopMenuItems = this.getMenuItems('desktop', path)
    const shouldShowMenuSearch = pathName === client.menu

    if (simple && !isAuthenticated) {
      return (
        <Fragment>
          <SimpleHeader
            serverError={serverError}
            homeUrl={mobileMenuItems[0].url}
            title={title}
            showWhiteHeader={this.shouldShowDoubleDeckerBar()}
            innerComponent={
              shouldShowMenuSearch && (
                <Box width="55%">
                  <SearchButton />
                </Box>
              )
            }
          />
          {hasLoginModal && this.renderLoginModal()}
        </Fragment>
      )
    }

    return (
      <div>
        <TestUserBannerContainer />
        <AppBanner />
        <FlagProblematicRendering label="headerRenderingProblem" />
        <span id={serverError ? 'mobileMenu' : null} data-testing="header">
          <button
            type="button"
            className={mobileMenuOpen ? css.overlayOpen : css.overlay}
            href={serverError ? '#' : null}
            onClick={this.hideMobileMenu}
          />
          <header className={css.header}>
            <div className={css.container}>
              <div
                className={classNames(css.mainBar, {
                  [css.menuHeader]: this.shouldShowDoubleDeckerBar(),
                })}
              >
                <div className={css.mainContent}>
                  <Link
                    to={homeElementMobile.url}
                    clientRouted={homeElementMobile.clientRouted && !promoCodeUrl}
                    className={css.logoLink}
                    aria-label="Gousto Home button"
                  >
                    <span>
                      {this.shouldShowDoubleDeckerBar() ? (
                        <Svg fileName="gousto-logo-red" className={css.logoDesktop} />
                      ) : (
                        <Svg fileName="gousto_logo" className={css.logoDesktop} />
                      )}
                    </span>
                  </Link>
                  {!isAuthenticated && shouldShowMenuSearch && (
                    <Box paddingRight={1} width="55%">
                      <SearchButton />
                    </Box>
                  )}
                  <span className={css.linkDesktopContainer}>
                    {!isAuthenticated && <RecipeBoxesLink />}
                    {!isAuthenticated && <HowItWorksDropdown />}
                    {!isAuthenticated && <BoxPricesLink />}
                    {this.renderMenuItems(desktopMenuItems, hideNav)}
                    {!isAuthenticated && <OurRecipesDropdown />}
                    {this.renderAuthLink()}
                  </span>
                  {isAuthenticated && pathName !== client.menu && (
                    <Button
                      color="secondary"
                      className={css.useAppCta}
                      onClick={this.onUseAppClick}
                    >
                      Use App
                    </Button>
                  )}
                  <MobileMenu
                    hideMobileMenu={this.hideMobileMenu}
                    onLoginClick={this.onLoginClick}
                    onLogoutClick={this.onLogoutClick}
                    showMobileMenu={this.showMobileMenu}
                    mobileMenuItems={mobileMenuItems}
                    mobileMenuOpen={mobileMenuOpen}
                    hideNav={hideNav}
                    isAuthenticated={isAuthenticated}
                    promoCodeUrl={promoCodeUrl}
                    trackNavigationClick={(trackingData) => {
                      trackNavigationClick(trackingData)
                      this.hideMobileMenu()
                    }}
                    serverError={serverError}
                    shouldRenderAccountLink={
                      isAuthenticated && pathName && pathName.startsWith('/menu')
                    }
                    isOnMenu={pathName && pathName.startsWith('/menu')}
                    doubleDeckerExperimentEnabled={doubleDeckerExperimentEnabled}
                  />
                </div>
              </div>
            </div>
          </header>
          {this.renderLoginModal()}
          <CancelOrderModalContainer close={this.onCloseCancelBoxModal} />
          <DuplicateOrderModalWrapperContainer />
          <OrderSkipOSRViewContainer />
          <SubPauseOSRViewContainer />
          {path.indexOf('my-') !== -1 ? (
            <div>
              <Account location={{ pathname: path }} />
            </div>
          ) : null}
        </span>
      </div>
    )
  }
}

Header.propTypes = {
  closeBoxModalVisibilityChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  fromJoin: PropTypes.bool,
  helpPreLoginVisibilityChange: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool,
  isHelpPreLoginOpen: PropTypes.bool,
  isLoginOpen: PropTypes.bool,
  loginVisibilityChange: PropTypes.func.isRequired,
  logoutUser: PropTypes.func.isRequired,
  path: PropTypes.string,
  promoCodeUrl: PropTypes.string,
  routing: PropTypes.objectOf(PropTypes.object).isRequired,
  serverError: PropTypes.bool,
  simple: PropTypes.bool,
  title: PropTypes.string,
  trackContinueAsNewCustomer: PropTypes.func,
  trackHelpPreLoginModalDisplayed: PropTypes.func,
  trackNavigationClick: PropTypes.func,
  hasLoginModal: PropTypes.bool,
  doubleDeckerExperimentEnabled: PropTypes.bool,
}

Header.defaultProps = {
  disabled: false,
  fromJoin: false,
  isAuthenticated: false,
  isHelpPreLoginOpen: false,
  isLoginOpen: false,
  path: '',
  promoCodeUrl: '',
  serverError: false,
  simple: false,
  title: '',
  trackContinueAsNewCustomer: () => {
  },
  trackHelpPreLoginModalDisplayed: () => {
  },
  trackNavigationClick: () => {
  },
  hasLoginModal: false,
  doubleDeckerExperimentEnabled: false,
}

export { Header }
